import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  AssignToRequest,
  UnassignRequest,
} from "../../services/prePlanningService.types";
import { http } from "../../utils/httpCommon";
import { useApiUrls } from "../useApiUrl";
import { toast } from "../../utils/snackbarHelper";
import { toastMessage } from "../../constants/strings";

type UseAssignShipmentsArgs = {
  onSuccess: (successInfo: {
    affectedShipmentsCount: number;
    shipmentIds: number[];
    sourceRouteId?: number;
    routeId?: number;
  }) => Promise<void> | void;
  onSettled?: () => void;
};

export const useAssignShipments = ({
  onSuccess,
  onSettled,
}: UseAssignShipmentsArgs) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { reassignShipments } = hookUrls;

  return useMutation({
    mutationFn: async (request: AssignToRequest) => {
      const url = reassignShipments;
      const { status } = await http.post(url, request);
      return status;
    },
    onSuccess: (_, variables) => {
      const uniqueShipmentIds = new Set(
        variables.origins.flatMap((s) => s.shipmentIds)
      );

      if (variables.origins.length > 0) {
        const sourceRouteId =
          variables.origins[0].type === "Route"
            ? Number(variables.origins[0].id)
            : undefined;
        const routeId =
          variables.target.type === "Route"
            ? Number(variables.target.id)
            : undefined;

        const affectedShipmentsCount = uniqueShipmentIds.size;

        onSuccess({
          affectedShipmentsCount,
          sourceRouteId,
          shipmentIds: [],
          routeId,
        });
      }
    },
    onError: () => toast(toastMessage.generics.error, { variant: "error" }),
    onSettled: () => {
      // Invalidating all queries because the list was long and complex: https://github.com/daylighttransport/operations-iol-webapp/pull/1370/files
      queryClient.invalidateQueries();
      onSettled?.();
    },
  });
};

export const useUnassignShipments = ({
  onSuccess,
  onSettled,
}: UseAssignShipmentsArgs) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { reassignShipments } = hookUrls;

  return useMutation({
    mutationFn: async (request: UnassignRequest) => {
      const url = reassignShipments;
      const { status } = await http.post(url, request);
      return status;
    },
    onSuccess: (_, variables) => {
      if (variables.origins.length > 0) {
        const routeId =
          variables.origins[0].type === "Route"
            ? Number(variables.origins[0].id)
            : undefined;
        const affectedShipmentsCount = variables.origins.flatMap(
          (origin) => origin.shipmentIds
        ).length;
        const shipmentIds = variables.origins.reduce(
          (acc: number[], origin) => acc.concat(origin.shipmentIds),
          []
        );
        onSuccess({
          shipmentIds,
          affectedShipmentsCount,
          routeId,
        });
      }
    },
    onError: () => toast(toastMessage.generics.error, { variant: "error" }),
    onSettled: () => {
      // Invalidating all queries because the list was long and complex: https://github.com/daylighttransport/operations-iol-webapp/pull/1370/files
      queryClient.invalidateQueries();
      onSettled?.();
    },
  });
};
